import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { QRCodeSVG } from "qrcode.react";
import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { authClient } from "../../lib/auth";
import { useMe } from "../../lib/me";
import { Button } from "../../ui/Button";
import { Field } from "../../ui/Field";
import { Input } from "../../ui/Input";

/** Two-factor setup (TOTP). Mandatory for organization admins (docs/05). */
export function MfaPage() {
  const { t } = useTranslation();
  const me = useMe();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [password, setPassword] = useState("");
  const [setup, setSetup] = useState<{ totpURI: string; backupCodes: string[] } | null>(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);

  if (me.user.twoFactorEnabled) {
    return (
      <div className="mx-auto max-w-md p-6 text-sm text-zinc-300">
        <p>{t("mfa.alreadyEnabled")}</p>
      </div>
    );
  }

  const start = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    const { data, error } = await authClient.twoFactor.enable({ password });
    if (error || !data || !("totpURI" in data)) return setError(t("auth.invalidCredentials"));
    setSetup({ totpURI: data.totpURI, backupCodes: data.backupCodes });
  };

  const verify = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    const { error } = await authClient.twoFactor.verifyTotp({ code });
    if (error) return setError(t("auth.invalidCode"));
    await queryClient.invalidateQueries({ queryKey: ["me"] });
    await navigate({ to: "/" });
  };

  return (
    <div className="mx-auto flex max-w-md flex-col gap-4 p-6 text-sm text-zinc-200">
      <h1 className="text-lg font-semibold">{t("mfa.title")}</h1>
      {me.mfaRequired && (
        <p className="rounded bg-amber-900/60 p-3 text-amber-100">{t("mfa.required")}</p>
      )}
      {!setup ? (
        <form onSubmit={start} className="flex flex-col gap-3">
          <Field label={t("auth.password")}>
            <Input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </Field>
          <Button type="submit" variant="primary">
            {t("mfa.start")}
          </Button>
        </form>
      ) : (
        <form onSubmit={verify} className="flex flex-col gap-3">
          <p>{t("mfa.scan")}</p>
          <div className="self-center rounded bg-white p-3">
            <QRCodeSVG value={setup.totpURI} size={192} />
          </div>
          <details className="text-xs text-zinc-400">
            <summary>{t("mfa.backupCodes")}</summary>
            <pre className="mt-2 rounded bg-zinc-900 p-2">{setup.backupCodes.join("\n")}</pre>
          </details>
          <Field label={t("auth.totpCode")}>
            <Input
              inputMode="numeric"
              required
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
          </Field>
          <Button type="submit" variant="primary">
            {t("mfa.verify")}
          </Button>
        </form>
      )}
      {error && <p className="text-red-400">{error}</p>}
    </div>
  );
}
